import type { WidgetCoreDefinition, WidgetCategory, WidgetTier } from './types';
import { startWidgetCore } from './start';
import { sayWidgetCore } from './say';
import { gatherWidgetCore } from './gather';
import { transferWidgetCore } from './transfer';
import { playWidgetCore } from './play';
import { conditionalWidgetCore } from './conditional';
import { httpRequestWidgetCore } from './httpRequest';
import { getCustomerIdWidgetCore } from './getCustomerId';
import { aiAgentWidgetCore } from './aiAgent';
import { hoursOfOperationWidgetCore } from './hoursOfOperation';
import { recordingWidgetCore } from './recording';
import { getLanguagePreferenceWidgetCore } from './getLanguagePreference';
import { transferToQueueWidgetCore } from './transferToQueue';
import { transferToAgentWidgetCore } from './transferToAgent';
import { voicemailWidgetCore } from './voicemail';

export class WidgetCoreRegistry {
  private widgets: Map<string, WidgetCoreDefinition> = new Map();
  private byCategory: Map<WidgetCategory, WidgetCoreDefinition[]> = new Map();
  private byTier: Map<WidgetTier, WidgetCoreDefinition[]> = new Map();

  register<TConfig>(widget: WidgetCoreDefinition<TConfig>): void {
    if (this.widgets.has(widget.id)) {
      const existing = this.widgets.get(widget.id)!;
      if (widget.version <= existing.version) {
        return;
      }
    }

    this.widgets.set(widget.id, widget as WidgetCoreDefinition);

    const categoryWidgets = this.byCategory.get(widget.category) || [];
    const filteredCategory = categoryWidgets.filter((w) => w.id !== widget.id);
    filteredCategory.push(widget as WidgetCoreDefinition);
    this.byCategory.set(widget.category, filteredCategory);

    const tierWidgets = this.byTier.get(widget.tier) || [];
    const filteredTier = tierWidgets.filter((w) => w.id !== widget.id);
    filteredTier.push(widget as WidgetCoreDefinition);
    this.byTier.set(widget.tier, filteredTier);
  }

  get(id: string): WidgetCoreDefinition | undefined {
    return this.widgets.get(id);
  }

  getAll(): WidgetCoreDefinition[] {
    return Array.from(this.widgets.values());
  }

  getByCategory(category: WidgetCategory): WidgetCoreDefinition[] {
    return this.byCategory.get(category) || [];
  }

  getByTier(tier: WidgetTier): WidgetCoreDefinition[] {
    return this.byTier.get(tier) || [];
  }

  has(id: string): boolean {
    return this.widgets.has(id);
  }

  count(): number {
    return this.widgets.size;
  }
}

export function createSharedRegistry(): WidgetCoreRegistry {
  const registry = new WidgetCoreRegistry();
  registry.register(startWidgetCore);
  registry.register(sayWidgetCore);
  registry.register(gatherWidgetCore);
  registry.register(transferWidgetCore);
  registry.register(playWidgetCore);
  registry.register(conditionalWidgetCore);
  registry.register(httpRequestWidgetCore);
  registry.register(getCustomerIdWidgetCore);
  registry.register(aiAgentWidgetCore);
  registry.register(hoursOfOperationWidgetCore);
  registry.register(recordingWidgetCore);
  registry.register(getLanguagePreferenceWidgetCore);
  registry.register(transferToQueueWidgetCore);
  registry.register(transferToAgentWidgetCore);
  registry.register(voicemailWidgetCore);
  return registry;
}
